import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import { AnimatePresence, motion } from 'framer-motion'
import { isEmpty, isNil } from 'ramda'
import { Fragment, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from 'styled-components'

import { FlexRow, Padding } from '@/@bothrs-ui/web/components/layout'
import { AppHeader } from '@/@bothrs-ui/web/components/navigation/header/AppHeader'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { Line } from '@/@bothrs-ui/web/components/primitives/line/Line'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H2 } from '@/@bothrs-ui/web/components/typography/Heading'
import { NativePlatform } from '@/capacitor/components/native-platform/NativePlatform'
import { globalDefinitions } from '@/defined-translations'
import { ContentPageLayout } from '@/layout/components/content-page-layout/ContentPageLayout'
import { AnimatedLoader } from '@/modules/shared/components/loader/Loader'
import { Empty } from '../../../shared/components/empty/Empty'
import { NotificationCard } from '../../components/notification-card/NotificationCard'
import { useNotificationCenter } from '../../hooks/useNotificationCenter'
import {
  StContainer,
  StContainerWithPagePadding,
  StMessageScrollContainer,
  StNotificationMessage,
  StTabItem,
} from './NotificationCenter.styled'

import type { NotificationMessage } from '../../types/notificiation-types'

const tabs = [
  {
    id: 'unread',
    title: t({
      id: 'notifications.tabs.unread',
      message: 'Ongelezen',
    }),
    filterFn: (notification: NotificationMessage) => !notification.read,
  },

  {
    id: 'all',
    title: t({
      id: 'notifications.tabs.all',
      message: 'Alle',
    }),
    filterFn: (notification: NotificationMessage) => !!notification,
  },
]

type Props = {
  onNotificationClick?: () => void
}

const NotificationCenterPage = ({ onNotificationClick }: Props) => {
  const theme = useTheme()
  const navigate = useNavigate()

  const [selectedIndex, setSelectedIndex] = useState(0)

  const { notifications, notificationsLoading } = useNotificationCenter()

  // Template variables
  // ------------------------------------------------------------------------- /
  const filteredNotifications: NotificationMessage[] = useMemo(() => {
    if (!isNil(notifications) && !isEmpty(notifications)) {
      const selectedTab = tabs.at(selectedIndex)
      if (!isNil(selectedTab)) {
        return notifications.filter(selectedTab.filterFn)
      }
    }

    return []
  }, [notifications, selectedIndex])

  // Render
  // -------------------------------------------------------------------------

  const renderNotificationMessage = (notification: NotificationMessage, index: number) => {
    const showDividerFullWidth =
      (index === 0 && !filteredNotifications[0].read) ||
      (!filteredNotifications[index].read && filteredNotifications[index - 1]?.read) ||
      (filteredNotifications[index].read && !filteredNotifications[index - 1]?.read)

    return (
      <Fragment key={notification.id}>
        {/* Top divider */}
        {(index === 0 && !filteredNotifications[0].read) || index < filteredNotifications.length ? (
          <Padding pl={showDividerFullWidth ? 0 : theme.UI.Spacing.Space20}>
            <Line color='#01224914' />
          </Padding>
        ) : null}

        <StNotificationMessage read={notification.read}>
          <StContainerWithPagePadding>
            <NotificationCard notification={notification} onClick={onNotificationClick} />
          </StContainerWithPagePadding>
        </StNotificationMessage>

        {/* Bottom devider */}
        {index === filteredNotifications.length - 1 && !notification.read ? <Line color='#01224914' /> : null}
      </Fragment>
    )
  }

  return notificationsLoading ? (
    <AnimatedLoader />
  ) : (
    <ContentPageLayout
      noPadding
      header={
        <NativePlatform>
          <StContainerWithPagePadding>
            <AppHeader
              primaryAction={{
                label: i18n._(globalDefinitions.goBack),
                icon: ['far', 'chevron-left'],
                onClick: () => {
                  navigate(-1)
                },
              }}
            />
          </StContainerWithPagePadding>
        </NativePlatform>
      }
    >
      <StContainer gap={theme.UI.SpacingPx.Space6}>
        <StContainerWithPagePadding>
          <H2 noMargin>
            {t({
              id: 'globals.notifications',
              comment: 'global for notifications',
              message: 'Meldingen',
            })}
          </H2>
        </StContainerWithPagePadding>

        {isNil(notifications) || isEmpty(notifications) ? (
          <StContainerWithPagePadding>
            <Empty
              title={t({
                id: 'notifications.inbox.empty.title',
                message: 'Geen meldingen',
              })}
              description={t({
                id: 'notifications.inbox.empty.description',
                message: 'Je ontvangt hier een overzicht van je documenten, afspraken en taken.',
              })}
            />
          </StContainerWithPagePadding>
        ) : (
          <>
            <StContainerWithPagePadding>
              <FlexRow gap={theme.UI.Spacing.Space4}>
                {tabs.map((tab, index) => {
                  const amount = notifications.filter(tab.filterFn)?.length || 0
                  const active = selectedIndex === index

                  return (
                    <StTabItem key={tab.id} active={active} onClick={() => setSelectedIndex(index)}>
                      <Span type={active ? 'BodyLargeBold' : 'BodyLargeRegular'}>{tab.title}</Span>

                      <Span type={active ? 'BodyLargeBold' : 'BodyLargeRegular'}>{amount}</Span>
                    </StTabItem>
                  )
                })}
              </FlexRow>
            </StContainerWithPagePadding>

            {isEmpty(filteredNotifications) ? (
              <StContainerWithPagePadding>
                <Empty
                  title={t({
                    id: 'notifications.inbox.empty-filtered.title',
                    message: 'Geen nieuwe meldingen',
                  })}
                  description={t({
                    id: 'notifications.inbox.empty.description',
                    message: 'Je ontvangt hier een overzicht van je documenten, afspraken en taken.',
                  })}
                  button={
                    <Button
                      variant='Secondary'
                      onClick={() => {
                        setSelectedIndex(1)
                      }}
                    >
                      {t({
                        id: 'notifications.index.empty.cta',
                        message: 'Toon alle meldingen',
                      })}
                    </Button>
                  }
                />
              </StContainerWithPagePadding>
            ) : (
              <StMessageScrollContainer>
                <AnimatePresence key='presence'>
                  {filteredNotifications.map((notification, index) => (
                    <Fragment key={notification.id}>
                      <motion.div
                        variants={{
                          hidden: {
                            opacity: 0,
                            height: 0,
                            scale: 0.8,
                            transition: {
                              height: {
                                delay: 0.2,
                              },
                            },
                          },
                          visible: { opacity: 1, height: 'auto', scale: 1 },
                        }}
                        transition={{
                          type: 'spring',
                          bounce: 0,
                          duration: 0.4,
                          delay: 0,
                        }}
                        initial={'hidden'}
                        animate={'visible'}
                        exit={'hidden'}
                      >
                        {renderNotificationMessage(notification, index)}
                      </motion.div>
                    </Fragment>
                  ))}
                </AnimatePresence>
              </StMessageScrollContainer>
            )}
          </>
        )}
      </StContainer>
    </ContentPageLayout>
  )
}

export { NotificationCenterPage }
